import { useState, useRef, type DragEvent } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import { useData } from '../store/data';
import { formatBytes } from '../lib/format';
import { useT, format } from '../i18n';

export type ImportMode = 'replace' | 'merge';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ImportCsvDialog({ open, onClose }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [mode, setMode] = useState<ImportMode>('replace');
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const importCsvs = useData(s => s.importCsvs);
  const t = useT();

  function reset() {
    setFiles([]);
    setSubmitting(false);
    setError(null);
    setResult(null);
    setDragOver(false);
  }

  function onPickFiles(picked: FileList | File[] | null) {
    if (!picked) return;
    const arr = Array.from(picked).filter(f => f.name.toLowerCase().endsWith('.csv'));
    setFiles(prev => [...prev, ...arr]);
    setError(null);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    onPickFiles(e.dataTransfer.files);
  }

  function removeFile(idx: number) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    if (!files.length) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await importCsvs(files, mode);
      const verb = mode === 'replace' ? t.import.verbReplaced : t.import.verbMerged;
      setResult(
        format(t.import.resultTemplate, { verb: `${r.total_before} → ${verb} ${r.total}`, count: `${r.added >= 0 ? '+' : ''}${r.added}` })
      );
      setFiles([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    if (submitting) return;
    onClose();
    setTimeout(reset, 200);
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
      <DialogTitle>{t.import.title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'divider',
              bgcolor: dragOver ? 'action.hover' : 'transparent',
              borderRadius: 3,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 120ms ease',
            }}
          >
            <UploadFileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body1">{t.import.dropzone}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t.import.dropzoneHint}
            </Typography>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              multiple
              hidden
              onChange={(e) => onPickFiles(e.target.files)}
            />
          </Box>

          {files.length > 0 && (
            <List dense sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
              {files.map((f, idx) => (
                <ListItem
                  key={`${f.name}-${idx}`}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => removeFile(idx)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon><InsertDriveFileIcon /></ListItemIcon>
                  <ListItemText primary={f.name} secondary={formatBytes(f.size)} />
                </ListItem>
              ))}
            </List>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>{t.import.modeQuestion}</Typography>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as ImportMode)}>
              <FormControlLabel value="replace" control={<Radio />} label={
                <Box>
                  <Typography variant="body2"><b>{t.import.modeReplace}</b></Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.import.modeReplaceHint}
                  </Typography>
                </Box>
              } />
              <FormControlLabel value="merge" control={<Radio />} label={
                <Box>
                  <Typography variant="body2"><b>{t.import.modeMerge}</b></Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.import.modeMergeHint}
                  </Typography>
                </Box>
              } />
            </RadioGroup>
          </Box>

          {submitting && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}
          {result && <Alert severity="success">{result}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={close} disabled={submitting}>{t.import.close}</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting || files.length === 0}
        >
          {submitting ? t.import.importing : format(t.import.importBtn, { count: files.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
